
// One widget INSTANCE of the dashboard editor: a catalog widget plus this
// instance's presentation state. The same catalog widget can be instantiated
// any number of times (e.g. one table and two charts of the same report).
// UserDashboardItem entities are only touched on APPLY.
class DashboardWidgetEntry
{
    id:string = null;                 // catalog widget id (NOT unique in the layout)
    title:string = null;
    supportedKinds:string[] = [];
    defaultKind:string = null;
    kind:string = null;               // current presentation: "header" | "chart" | "table"
    defaultChartType:number = null;
    chartType:number = null;
    supportedFilters:any[] = [];      // catalog SupportedFilters descriptors
    chartXAxes:any[] = [];            // catalog ChartXAxes / ChartYAxes choices
    chartYAxes:any[] = [];
    customTitle:string = null;        // per-instance title override
    autoTitle:string = null;          // title derived from the filter selection (not persisted as such)
    filterValues = {};                // filter Key -> selected entity identifier
    xKey:string = null;               // chart axis choices (null = widget default)
    yKey:string = null;
    item:UserDashboardItem = null;    // null until APPLY inserts one
}

// A group is one dashboard row; its widgets render side by side.
class DashboardEditGroup
{
    widgets:DashboardWidgetEntry[] = [];
}

// Flat table row model: group headers with their widget instances as sub cells
enum DashboardEditRowKind
{
    Group,
    Widget
}

class DashboardEditViewController extends MUIViewController
{
    static newInstance() : DashboardEditViewController {
        let vc = new DashboardEditViewController("dashboard-edit-view");
        vc.initWithResource("layout/reports/DashboardEditView.html");
        return vc;
    }

    get preferredContentSize(): MIOSize {
        return new MIOSize(420, 560);
    }

    delegate:any = null;

    private addGroupButton:MUIButton = null;
    private applyButton:MUIButton = null;
    private tableView:UITableView = null;
    viewDidLoad() {
        this.addGroupButton = MUIOutlet(this, "add-group-btn", "MUIButton");
        this.addGroupButton.setAction(this, function(this:DashboardEditViewController){
            this.groups.push(new DashboardEditGroup());
            this.updateUI();
        });

        this.applyButton = MUIOutlet(this, "apply-btn", "MUIButton");
        this.applyButton.setAction(this, this.applyAction);

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
        this.tableView.setEditing(true);
    }

    viewWillAppear(animated?: any): void {
        super.viewWillAppear(animated);
        this.updateUI();
    }

    private groups:DashboardEditGroup[] = [];
    private catalogWidgets:any[] = [];
    private widgetsByID = {};
    // Entities to delete on APPLY: instances the user removed while editing,
    // plus legacy rows (hidden ones from the old show/hide editor, retired widgets)
    private removedItems:UserDashboardItem[] = [];
    setCatalog(catalog:any) {
        this.buildGroups(catalog);
        this.updateUI();
    }

    private newEntry(widget:any):DashboardWidgetEntry {
        let entry = new DashboardWidgetEntry();
        entry.id = widget["ID"];
        entry.title = widget["Title"] ?? widget["ID"];
        entry.supportedKinds = widget["SupportedKinds"] ?? [widget["Kind"]];
        entry.defaultKind = widget["Kind"];
        entry.kind = entry.defaultKind;
        entry.defaultChartType = widget["DefaultChartType"] ?? null;
        entry.chartType = entry.defaultChartType;
        entry.supportedFilters = widget["SupportedFilters"] ?? [];
        entry.chartXAxes = widget["ChartXAxes"] ?? [];
        entry.chartYAxes = widget["ChartYAxes"] ?? [];
        return entry;
    }

    // Resolves the user's UserDashboardItem rows — one widget instance each —
    // into groups (dashboard rows). A user without instances starts from the
    // catalog default layout, materialized as fresh instances on APPLY.
    private buildGroups(catalog:any) {
        this.catalogWidgets = catalog["Widgets"] ?? [];
        this.widgetsByID = {};
        for (let w of this.catalogWidgets) this.widgetsByID[w["ID"]] = w;
        this.removedItems = [];

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let userItems = ad.selectedUser.dashboardItems;

        let entries = [];
        for (let index = 0; index < (userItems?.count ?? 0); index++){
            let item = userItems.objectAtIndex(index) as UserDashboardItem;
            let widget = item.widgetID != null ? this.widgetsByID[item.widgetID] : null;
            if (widget == null || item.hidden == true) {
                // retired widget or legacy hidden row: clean it up on APPLY
                this.removedItems.push(item);
                continue;
            }
            let entry = this.newEntry(widget);
            entry.item = item;
            (entry as any).order = item.orderIndex;
            (entry as any).rowBreak = item.rowBreak == true;
            if (item.config != null) {
                try {
                    let config = JSON.parse(item.config);
                    if (entry.supportedKinds.containsObject(config["kind"])) entry.kind = config["kind"];
                    if (entry.supportedKinds.containsObject("chart") && config["chart_type"] != null) entry.chartType = config["chart_type"];
                    if (typeof config["title"] == "string" && config["title"].length > 0) entry.customTitle = config["title"];
                    let filters = config["filters"];
                    if (filters != null) {
                        for (let f of entry.supportedFilters) {
                            let values = filters[f["Key"]];
                            if (values instanceof Array && values.length > 0) entry.filterValues[f["Key"]] = values[0];
                            else if (typeof values == "string") entry.filterValues[f["Key"]] = values;
                        }
                    }
                    let axisKey = function(axes:any[], value:any) {
                        for (let axis of axes) { if (axis["Key"] == value) return value; }
                        return null;
                    };
                    entry.xKey = axisKey(entry.chartXAxes, config["x_key"]);
                    entry.yKey = axisKey(entry.chartYAxes, config["y_key"]);
                } catch { /* malformed config: keep defaults */ }
            }
            entries.push(entry);
        }

        if (entries.length == 0) {
            entries = [];
            for (let row of (catalog["DefaultSpec"]?.["rows"] ?? [])) {
                let widgets = row["widgets"] ?? [];
                for (let wi = 0; wi < widgets.length; wi++){
                    let widget = this.widgetsByID[widgets[wi]["id"]];
                    if (widget == null) continue;
                    let entry = this.newEntry(widget);
                    (entry as any).rowBreak = wi == 0;
                    entries.push(entry);
                }
            }
        }
        else {
            entries.sort(function(a, b){ return (a as any).order - (b as any).order; });
        }

        this.groups = [];
        let currentGroup:DashboardEditGroup = null;
        for (let entry of entries) {
            if (currentGroup == null || (entry as any).rowBreak == true) {
                currentGroup = new DashboardEditGroup();
                this.groups.push(currentGroup);
            }
            currentGroup.widgets.push(entry);
        }
    }

    private updateUI(){
        if (this.viewIsLoaded == false) return;
        this.tableView.reloadData();
    }

    // The options popover edits entries live; it calls this on close so the
    // cell labels (title + presentation suffix) reflect the changes
    updateUIFromConfig(){
        this.updateUI();
    }

    // Flat row list: [group, widget...]*
    private rows():any[] {
        let rows = [];
        for (let gi = 0; gi < this.groups.length; gi++){
            rows.push({"kind": DashboardEditRowKind.Group, "groupIndex": gi});
            for (let entry of this.groups[gi].widgets) {
                rows.push({"kind": DashboardEditRowKind.Widget, "entry": entry, "groupIndex": gi});
            }
        }
        return rows;
    }

    numberOfSections(tableView:UITableView){
        return 1;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        return this.rows().length;
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        let row = this.rows()[indexPath.row];

        if (row["kind"] == DashboardEditRowKind.Widget) {
            let cell = tableView.dequeueReusableCellWithIdentifier("WidgetCell") as DashboardEditWidgetCell;
            cell.owner = this;
            cell.setEntry(row["entry"], row["groupIndex"]);
            return cell;
        }

        let cell = tableView.dequeueReusableCellWithIdentifier("GroupCell") as DashboardEditGroupCell;
        cell.owner = this;
        cell.setGroup(row["groupIndex"], this.catalogWidgets);
        return cell;
    }

    // MARK: - Drag & drop reordering

    canMoveRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath):boolean {
        // group headers drag whole rows; widgets drag between groups
        return this.rows()[indexPath.row] != null;
    }

    // proposed comes in coordinates that assume the dragged row was removed first
    targetIndexPathForMoveFromRowAtIndexPath(tableView:UITableView, from:MIOIndexPath, proposed:MIOIndexPath):MIOIndexPath {
        let flat = this.rows();
        let source = flat[from.row];
        if (source == null) return null;

        let rest = flat.slice();
        rest.splice(from.row, 1);

        if (source["kind"] == DashboardEditRowKind.Widget) {
            // a widget can never sit before the first group header
            if (proposed.row < 1) return MIOIndexPath.indexForRowInSection(1, proposed.section);
            return proposed;
        }

        // group header: snap to group boundaries (before another group header,
        // or after the last row)
        let slots = [rest.length];
        for (let index = 0; index < rest.length; index++){
            if (rest[index]["kind"] == DashboardEditRowKind.Group) slots.push(index);
        }
        let best = slots[0];
        for (let slot of slots) {
            if (Math.abs(slot - proposed.row) < Math.abs(best - proposed.row)) best = slot;
        }
        return MIOIndexPath.indexForRowInSection(best, proposed.section);
    }

    moveRowAtIndexPath(tableView:UITableView, from:MIOIndexPath, to:MIOIndexPath){
        let flat = this.rows();
        let source = flat[from.row];
        if (source == null) return;

        let rest = flat.slice();
        rest.splice(from.row, 1);

        if (source["kind"] == DashboardEditRowKind.Widget) {
            let entry = source["entry"] as DashboardWidgetEntry;

            // destination: walk the post-removal rows up to the drop position
            let destGroup = -1;
            let destPos = 0;
            for (let index = 0; index < to.row && index < rest.length; index++){
                if (rest[index]["kind"] == DashboardEditRowKind.Group) { destGroup = rest[index]["groupIndex"]; destPos = 0; }
                else destPos++;
            }
            if (destGroup < 0) return;

            this.groups[source["groupIndex"]].widgets.removeObject(entry);
            this.groups[destGroup].widgets.splice(destPos, 0, entry);
        }
        else {
            // group header: its widgets follow on reload
            let slot = 0;
            for (let index = 0; index < to.row && index < rest.length; index++){
                if (rest[index]["kind"] == DashboardEditRowKind.Group) slot++;
            }
            let group = this.groups[source["groupIndex"]];
            this.groups.splice(source["groupIndex"], 1);
            this.groups.splice(slot, 0, group);
        }

        this.updateUI();
    }

    // MARK: - Add / remove instances

    // Plus button on the group header: push the add screen (report + kind).
    // NOT an alert — presenting one dismisses the popover underneath and the
    // un-applied edits die with it.
    showAddWidget(groupIndex:number){
        let vc = DashboardAddWidgetViewController.newInstance();
        vc.setContext(this, groupIndex, this.catalogWidgets);
        this.navigationController?.pushViewController(vc, true);
    }

    addWidget(groupIndex:number, widgetID:string, kind:string){
        if (groupIndex < 0 || groupIndex >= this.groups.length) return;
        let widget = this.widgetsByID[widgetID];
        if (widget == null) return;
        let entry = this.newEntry(widget);
        if (entry.supportedKinds.containsObject(kind)) entry.kind = kind;
        this.groups[groupIndex].widgets.push(entry);
        this.updateUI();
    }

    // Deletes the instance (the entity goes away on APPLY)
    removeWidget(entry:DashboardWidgetEntry, groupIndex:number){
        if (groupIndex < 0 || groupIndex >= this.groups.length) return;
        this.groups[groupIndex].widgets.removeObject(entry);
        if (entry.item != null) this.removedItems.push(entry.item);
        this.updateUI();
    }

    // Removing a group deletes every instance in it
    removeGroup(groupIndex:number){
        if (groupIndex < 0 || groupIndex >= this.groups.length) return;
        for (let entry of this.groups[groupIndex].widgets) {
            if (entry.item != null) this.removedItems.push(entry.item);
        }
        this.groups.splice(groupIndex, 1);
        this.updateUI();
    }

    // Info button on a widget cell: push the per-instance options screen
    showWidgetConfig(entry:DashboardWidgetEntry, sourceButton:MUIButton){
        let vc = DashboardWidgetConfigViewController.newInstance();
        vc.editor = this;
        // column changes re-render the dashboard behind the editor
        vc.reportDelegate = this.delegate;
        vc.setEntry(entry);
        if (this.navigationController != null) this.navigationController.pushViewController(vc, true);
        else AppHelper.sharedInstance().presentPopOverViewController(vc, sourceButton, true);
    }

    private applyAction(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let order = 0;
        let applyEntry = function(entry:DashboardWidgetEntry, rowBreak:boolean){
            let configValues = {};
            if (entry.kind != entry.defaultKind) configValues["kind"] = entry.kind;
            if (entry.kind == "chart" && entry.chartType != entry.defaultChartType) configValues["chart_type"] = entry.chartType;
            if (entry.customTitle != null) configValues["title"] = entry.customTitle;
            let filters = {};
            for (let key in entry.filterValues) filters[key] = [entry.filterValues[key]];
            if (Object.keys(filters).length > 0) configValues["filters"] = filters;
            // axis choices are stored only when they differ from the widget default
            if (entry.xKey != null && entry.chartXAxes.length > 1 && entry.xKey != entry.chartXAxes[0]["Key"]) configValues["x_key"] = entry.xKey;
            if (entry.yKey != null && entry.chartYAxes.length > 1 && entry.yKey != entry.chartYAxes[0]["Key"]) configValues["y_key"] = entry.yKey;
            let config = Object.keys(configValues).length > 0 ? JSON.stringify(configValues) : null;

            let item = entry.item;
            if (item == null) {
                item = MIOEntityDescription.insertNewObjectForEntityForName("UserDashboardItem", ad.managedObjectContext) as UserDashboardItem;
                item.widgetID = entry.id;
                item.user = ad.selectedUser;
                entry.item = item;
            }
            item.hidden = false;
            item.orderIndex = order++;
            item.rowBreak = rowBreak;
            item.config = config;
        };

        for (let group of this.groups) {
            for (let index = 0; index < group.widgets.length; index++){
                applyEntry(group.widgets[index], index == 0);
            }
        }
        for (let item of this.removedItems) ad.managedObjectContext.deleteObject(item);
        this.removedItems = [];

        DBHelper.saveMainContext();
        if (this.delegate != null) this.delegate.applyDashboardOptions();
        this.dismissViewController(true);
    }
}

class DashboardEditGroupCell extends UITableViewCell
{
    owner:DashboardEditViewController = null;

    private titleLabel:MUILabel = null;
    private removeButton:MUIButton = null;
    private addWidgetButton:MUIButton = null;

    awakeFromHTML(): void {
        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        // remove = delete the row and every widget instance in it
        this.removeButton = MUIOutlet(this, "remove-btn", "MUIButton");
        this.removeButton.setAction(this, function(this:DashboardEditGroupCell){
            if (this._groupIndex >= 0) this.owner?.removeGroup(this._groupIndex);
        });

        // a stale cached cell layout may predate this outlet: degrade, don't throw
        this.addWidgetButton = MUIOutlet(this, "add-widget-btn", "MUIButton");
        this.addWidgetButton?.setAction(this, function(this:DashboardEditGroupCell){
            if (this._groupIndex >= 0) this.owner?.showAddWidget(this._groupIndex);
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _groupIndex = -1;
    setGroup(groupIndex:number, catalogWidgets:any[]){
        this._groupIndex = groupIndex;
        this.titleLabel.text = MIOLocalizeString("GROUP", "GROUP") + " " + (groupIndex + 1);
    }
}

class DashboardEditWidgetCell extends UITableViewCell
{
    owner:DashboardEditViewController = null;

    private titleLabel:MUILabel = null;
    private removeButton:MUIButton = null;
    private configButton:MUIButton = null;

    awakeFromHTML(): void {
        this.titleLabel = MUIOutlet(this, "title-lbl", "MUILabel");

        // remove = delete this widget instance
        this.removeButton = MUIOutlet(this, "remove-btn", "MUIButton");
        this.removeButton.setAction(this, function(this:DashboardEditWidgetCell){
            this.owner?.removeWidget(this._entry, this._groupIndex);
        });

        // a stale cached cell layout may predate this outlet: degrade, don't throw
        this.configButton = MUIOutlet(this, "config-btn", "MUIButton");
        this.configButton?.setAction(this, function(this:DashboardEditWidgetCell){
            this.owner?.showWidgetConfig(this._entry, this.configButton);
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    // MUIChartViewType order: Bar=0, HorizontalBar=1, Line=2, Pie=3
    static chartTypeTitles = ["CHART: BAR", "CHART: HBAR", "CHART: LINE", "CHART: PIE"];

    static presentationTitle(entry:DashboardWidgetEntry):string {
        if (entry.kind == "chart") {
            let title = DashboardEditWidgetCell.chartTypeTitles[entry.chartType] ?? "CHART";
            return MIOLocalizeString(title, title);
        }
        let title = entry.kind.toUpperCase();
        return MIOLocalizeString(title, title);
    }

    private _entry:DashboardWidgetEntry = null;
    private _groupIndex = -1;
    setEntry(entry:DashboardWidgetEntry, groupIndex:number){
        this._entry = entry;
        this._groupIndex = groupIndex;

        // the presentation now reads as a suffix; it is edited in the options
        // popover (the info button), not inline
        let title = entry.customTitle ?? MIOLocalizeString(entry.title, entry.title);
        if (entry.supportedKinds.length > 1 || entry.kind == "chart") {
            title += " · " + DashboardEditWidgetCell.presentationTitle(entry);
        }
        this.titleLabel.text = title;
    }
}
